import { 
  Room, 
  RoomEvent, 
  RemoteParticipant, 
  RemoteTrackPublication, 
  RemoteTrack, 
  ConnectionState,
  DisconnectReason
} from "livekit-client";

export type ConnectionStateCallback = (state: ConnectionState) => void;
export type ParticipantCallback = (participant: RemoteParticipant) => void;

interface LiveKitServiceCallbacks {
  onConnectionStateChange?: ConnectionStateCallback;
  onParticipantConnected?: ParticipantCallback;
  onParticipantDisconnected?: ParticipantCallback;
  onDisconnected?: (reason?: DisconnectReason) => void;
}

class LiveKitService {
  private room: Room | null = null;
  private static instance: LiveKitService;
  private callbacks: LiveKitServiceCallbacks = {};
  private connectionState: ConnectionState = ConnectionState.Disconnected;

  private constructor() {}

  public static getInstance(): LiveKitService {
    if (!LiveKitService.instance) {
      LiveKitService.instance = new LiveKitService();
    }
    return LiveKitService.instance;
  }

  /**
   * Set callbacks for room events
   */
  public setCallbacks(callbacks: LiveKitServiceCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Clear all callbacks
   */
  public clearCallbacks(): void {
    this.callbacks = {};
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  public async connectToRoom(url: string, token: string): Promise<Room> {
    if (this.room) {
      if (this.room.state === "connected") {
        console.log("[LiveKitService] Already connected, disconnecting first...");
        this.disconnect();
      }
    }

    try {
      console.log("[LiveKitService] Creating new room instance...");
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
        // Reconnection settings
        disconnectOnPageLeave: true,
      });

      this.setupRoomListeners();

      console.log("[LiveKitService] Connecting to:", url);
      await this.room.connect(url, token);
      
      this.connectionState = ConnectionState.Connected;
      console.log("[LiveKitService] ✓ Connected to room:", this.room.name);
      
      return this.room;

    } catch (error) {
      console.error("[LiveKitService] ✗ Failed to connect:", error);
      this.connectionState = ConnectionState.Disconnected;
      throw error;
    }
  }

  public disconnect(): void {
    if (this.room) {
      console.log("[LiveKitService] Disconnecting from room...");
      this.room.disconnect();
      this.room = null;
      this.connectionState = ConnectionState.Disconnected;
      console.log("[LiveKitService] ✓ Disconnected");
    }
  }

  public getRoom(): Room | null {
    return this.room;
  }

  /**
   * Get all participants (including local)
   */
  public getParticipants(): RemoteParticipant[] {
    if (!this.room) return [];
    return Array.from(this.room.remoteParticipants.values());
  }

  /**
   * Get participant count
   */
  public getParticipantCount(): number {
    if (!this.room) return 0;
    return this.room.remoteParticipants.size + 1; // +1 for local
  }

  public async toggleMic(enabled: boolean): Promise<void> {
    if (this.room?.localParticipant) {
      console.log(`[LiveKitService] Setting mic: ${enabled}`);
      await this.room.localParticipant.setMicrophoneEnabled(enabled);
    }
  }

  public async toggleCamera(enabled: boolean): Promise<void> {
    if (this.room?.localParticipant) {
      console.log(`[LiveKitService] Setting camera: ${enabled}`);
      await this.room.localParticipant.setCameraEnabled(enabled);
    }
  }

  public async toggleScreenShare(enabled: boolean): Promise<void> {
    if (this.room?.localParticipant) {
      console.log(`[LiveKitService] Setting screen share: ${enabled}`);
      await this.room.localParticipant.setScreenShareEnabled(enabled);
    }
  }

  private setupRoomListeners(): void {
    if (!this.room) return;

    this.room
      .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        console.log("[LiveKitService] Connection state:", state);
        this.connectionState = state;
        this.callbacks.onConnectionStateChange?.(state);
      })
      .on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        console.log("[LiveKitService] Participant joined:", participant.identity);
        this.callbacks.onParticipantConnected?.(participant);
      })
      .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        console.log("[LiveKitService] Participant left:", participant.identity);
        this.callbacks.onParticipantDisconnected?.(participant);
      })
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
        console.log("[LiveKitService] Track subscribed:", track.kind, "from", participant.identity);
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
        console.log("[LiveKitService] Track unsubscribed:", track.kind, "from", participant.identity);
      })
      .on(RoomEvent.Reconnecting, () => {
        console.log("[LiveKitService] Reconnecting...");
        this.connectionState = ConnectionState.Reconnecting;
        this.callbacks.onConnectionStateChange?.(ConnectionState.Reconnecting);
      })
      .on(RoomEvent.Reconnected, () => {
        console.log("[LiveKitService] ✓ Reconnected");
        this.connectionState = ConnectionState.Connected;
        this.callbacks.onConnectionStateChange?.(ConnectionState.Connected);
      })
      .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        console.log("[LiveKitService] Disconnected, reason:", reason);
        this.connectionState = ConnectionState.Disconnected;
        this.callbacks.onDisconnected?.(reason);
      });
  }
}

export const livekitService = LiveKitService.getInstance();
export default livekitService;

